/*
  WOOF core. Loaded once (deferred) via snippets/wf-head.liquid.
  Global helpers for all wf-* sections:
    WOOF.fit         script sublines that never wrap (P02)
    WOOF.scroll      one shared rAF scroll loop for scroll-coupled effects (P03)
    WOOF.flow        background flow between sections (P03)
    WOOF.scrollRows  fade hint on horizontally scrolling rows (P03)
    WOOF.money       shop-currency amounts converted to the visitor's currency (P04)
    WOOF.rainbow     scroll-coupled rainbow sweep through text (P10)
    reveal           fade-up of wf sections below the fold (P10)
  Flags come from the wf-global section (window.WOOF.fx, window.WOOF.bgFlow) and are off for reduced motion.
*/
(() => {
  const W = (window.WOOF = window.WOOF || {});

  /*
    20.10. (customer): browsers draw the focus ring when a popup moves focus on open (email field, close button),
    also after a tap. Rings show only for keyboard users: Tab adds html.wf-kbd, a pointer/touch removes it
    (wf-core.css hides outlines without it).
  */
  const docEl = document.documentElement;
  document.addEventListener('keydown', (e) => { if (e.key === 'Tab') docEl.classList.add('wf-kbd'); }, true);
  document.addEventListener('pointerdown', () => docEl.classList.remove('wf-kbd'), true);

  /*
    Script fit: Mr Dafoe sublines never wrap (HANDOFF §2). The section sets the desired size
    (usually clamp()); if the line would be wider than its container, shrink it until it fits.
      data-wf-fit            on the .wf-script element
      data-wf-fit-min="18"   optional lower bound in px (default 12)
  */
  const fitTargets = new Set();

  function fitScript(el) {
    const box = el.parentElement;
    if (!box) return;
    // Restore the size the section set (inline or via CSS) before measuring.
    if (el.dataset.wfFitBase === undefined) el.dataset.wfFitBase = el.style.fontSize;
    el.style.fontSize = el.dataset.wfFitBase;
    const styles = getComputedStyle(box);
    const available = box.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    const natural = el.scrollWidth;
    if (available <= 0 || natural <= available) return;
    const min = parseFloat(el.dataset.wfFitMin) || 12;
    const current = parseFloat(getComputedStyle(el).fontSize);
    el.style.fontSize = `${Math.max(min, Math.floor(current * (available / natural) * 100) / 100)}px`;
  }

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      for (const el of fitTargets) if (el.parentElement === entry.target) fitScript(el);
    }
  });

  function initFit(root = document) {
    root.querySelectorAll('[data-wf-fit]').forEach((el) => {
      if (!fitTargets.has(el)) {
        fitTargets.add(el);
        if (el.parentElement) resizeObserver.observe(el.parentElement);
      }
      fitScript(el);
    });
  }

  function refitAll() {
    for (const el of fitTargets) {
      if (el.isConnected) fitScript(el);
      else fitTargets.delete(el);
    }
  }

  W.fit = { init: initFit, refit: refitAll };

  // Run after load, again once fonts are in (Mr Dafoe changes the width), and after theme editor reloads.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFit());
  } else {
    initFit();
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refitAll);
  document.addEventListener('shopify:section:load', (event) => initFit(event.target));

  /*
    Scroll loop: one shared requestAnimationFrame loop for every scroll-coupled effect (HANDOFF §6, §8).
    Effects are pure functions of the scroll position, so they also run backwards.
      const off = WOOF.scroll.add((state) => { ...; return needsAnotherFrame; });
      state = { y, vh, vw }
    A handler returns true to request one more frame (e.g. while easing towards a target).
    Effects decide themselves whether they run: check WOOF.fx.enabled / WOOF.fx.<name> before registering.
  */
  const handlers = new Set();
  let frame = 0;

  /*
    Horizon 4.2 scrolls .page-wrapper instead of the window from 990px up (base.css, theme drawer);
    below that the window scrolls. Always read the position from whichever element scrolls right now.
  */
  function scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY) && wrapper.scrollHeight > wrapper.clientHeight) {
      return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  function tick() {
    frame = 0;
    const state = { y: scroller().scrollTop, vh: window.innerHeight, vw: window.innerWidth };
    let again = false;
    for (const handler of handlers) {
      try {
        if (handler(state) === true) again = true;
      } catch (error) {
        console.error('[WOOF] scroll handler failed', error);
        handlers.delete(handler);
      }
    }
    if (again) request();
  }

  function request() {
    if (!frame && handlers.size && !document.hidden) frame = requestAnimationFrame(tick);
  }

  // Capture phase: scroll events do not bubble, this catches the window and .page-wrapper alike.
  document.addEventListener('scroll', request, { passive: true, capture: true });
  window.addEventListener('resize', request, { passive: true });
  document.addEventListener('visibilitychange', request);

  W.scroll = {
    add(handler) {
      handlers.add(handler);
      request();
      return () => handlers.delete(handler);
    },
    request,
    scroller,
    /** Progress of an element through the viewport: 0 = top edge enters at the bottom, 1 = bottom edge leaves at the top. */
    progress(el, state) {
      const rect = el.getBoundingClientRect();
      const total = rect.height + state.vh;
      return Math.min(1, Math.max(0, (state.vh - rect.top) / total));
    },
  };

  /*
    Background flow (HANDOFF §4, Home DC "flowRef"): very light accent tints blend from section to section.
    Each WOOF section that takes part sets data-wf-flow="#F1F6D2" (its tint, a section setting).
    The page color at 20 % and 90 % of the viewport height is interpolated between section centers
    (smoothstep) and eased (factor .06), painted on one fixed layer behind the page.
    On when WOOF.bgFlow (wf-global: setting + template exclusion + reduced motion).
  */
  const hexToRgb = (hex) => {
    const value = String(hex || '').trim().replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(value)) return [255, 255, 255];
    return [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16));
  };

  function initFlow() {
    if (!W.bgFlow || W.flow) return;
    const sections = () => [...document.querySelectorAll('[data-wf-flow]')];
    if (!sections().length) return;

    const layer = document.createElement('div');
    layer.className = 'wf-flow';
    layer.setAttribute('aria-hidden', 'true');
    document.body.prepend(layer);

    let stops = null;
    let measuredAt = 0;
    let current = null;
    const measure = () => {
      measuredAt = Date.now();
      const y = scroller().scrollTop;
      // 20.10.: hidden or empty sections (e.g. no reviews/UGC on the gift card) report top 0 and pulled their
      // tint to the top of the page; only sections with a height count
      stops = sections()
        .map((el) => {
          const rect = el.getBoundingClientRect();
          return rect.height > 0 ? { y: rect.top + y + rect.height / 2, c: hexToRgb(el.dataset.wfFlow) } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.y - b.y);
    };
    const colorAt = (y) => {
      if (!stops.length) return [255, 255, 255];
      if (y <= stops[0].y) return stops[0].c;
      for (let i = 1; i < stops.length; i++) {
        if (y < stops[i].y) {
          let t = (y - stops[i - 1].y) / (stops[i].y - stops[i - 1].y);
          t = t * t * (3 - 2 * t);
          return stops[i - 1].c.map((v, k) => v + (stops[i].c[k] - v) * t);
        }
      }
      return stops[stops.length - 1].c;
    };
    const rgb = (c) => `rgb(${c.map(Math.round).join(',')})`;

    const paint = (state) => {
      // DC: re-measure at most every 1.5s. Lazy images (PDP gallery) make the page longer after load; with
      // .page-wrapper scrolling, the body never resizes, so old positions let the flow run ahead (Bugreport 2 D).
      if (!stops || Date.now() - measuredAt > 1500) measure();
      const targets = [colorAt(state.y + state.vh * 0.2), colorAt(state.y + state.vh * 0.9)];
      if (!current) current = targets.map((c) => c.slice());
      let delta = 0;
      targets.forEach((target, j) =>
        target.forEach((v, k) => {
          const next = current[j][k] + (v - current[j][k]) * 0.06;
          delta += Math.abs(v - next);
          current[j][k] = next;
        })
      );
      layer.style.background =
        'radial-gradient(60% 50% at 50% 35%, rgba(255,255,255,.7), rgba(255,255,255,0) 70%),' +
        `linear-gradient(180deg, ${rgb(current[0])} 0%, ${rgb(current[1])} 100%)`;
      return delta > 0.5;
    };

    // Section positions change with images, fonts and editor edits: re-measure on those signals.
    const invalidate = () => {
      stops = null;
      request();
    };
    window.addEventListener('resize', invalidate, { passive: true });
    window.addEventListener('load', invalidate);
    const ro = new ResizeObserver(invalidate);
    ro.observe(document.body);
    const content = document.querySelector('main') || document.querySelector('.page-wrapper');
    if (content) ro.observe(content);
    document.addEventListener('shopify:section:load', invalidate);
    document.addEventListener('shopify:section:unload', invalidate);

    W.flow = { invalidate };
    W.scroll.add(paint);
  }

  /*
    Horizontal scroll rows (mobile): toggle the right-edge fade off once the row is scrolled to its end.
      <div class="wf-scroll-x" data-wf-scroll-x>…</div>
  */
  function initScrollRows(root = document) {
    root.querySelectorAll('[data-wf-scroll-x]').forEach((row) => {
      if (row.dataset.wfScrollBound) return;
      row.dataset.wfScrollBound = 'true';
      const update = () => {
        const end = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
        row.classList.toggle('is-at-end', end);
      };
      row.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(row);
      update();
    });
  }

  W.scrollRows = { init: initScrollRows };

  /*
    Money in the visitor's currency. Amounts from WOOF Config (free shipping threshold) are stored in the
    shop currency; Liquid can format but not convert them. Convert with Shopify's live rate, round to whole units.
      <span data-wf-money="7500">$75</span>   (cents in shop currency)
    data-wf-money-nice: round to a threshold a local would expect, one significant digit ($30 → €27 → €30, $50 → €45 → €50, £23 → £20).
  */
  function niceAmount(x) {
    if (x < 10) return Math.round(x);
    const m = 10 ** Math.floor(Math.log10(x));
    // Slight upward bias so €44.5 ($50) reads €50, not €40; £38 → £40, CA$43 → CA$40.
    return Math.round(x / m + 0.15) * m;
  }

  function formatMoney(cents, nice = false) {
    const currency = (window.Shopify && Shopify.currency) || {};
    const rate = parseFloat(currency.rate) || 1;
    const code = currency.active || 'USD';
    const locale = [(window.Shopify && Shopify.locale) || 'en', window.Shopify && Shopify.country].filter(Boolean).join('-');
    const raw = (cents / 100) * rate;
    const amount = nice ? niceAmount(raw) : Math.round(raw);
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code, maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(amount);
    } catch (error) {
      return `${amount} ${code}`;
    }
  }

  function initMoney(root = document) {
    root.querySelectorAll('[data-wf-money]').forEach((el) => {
      const cents = parseFloat(el.dataset.wfMoney);
      if (!Number.isNaN(cents)) el.textContent = formatMoney(cents, el.hasAttribute('data-wf-money-nice'));
    });
  }

  W.money = { format: formatMoney, init: initMoney };

  /*
    Rainbow sweep (HANDOFF §6, Home DC RB()/clipT()): light brand shades in rainbow order run through the text,
    position = scroll. Text keeps its own color when effects are off.
      <span data-wf-rainbow data-wf-rainbow-base="#FAFAF7">love dogs.</span>
    Progress: 0 → 1 over the first 60 % of the viewport height of scrolling (hero).
    Optional per element: data-wf-rainbow-stops="#F9C6DB 42%, #F7A29A 46%, …" (own shades between the base color,
    Custom LP DC) and data-wf-rainbow-distance="0.25" (scroll distance in viewport heights).
  */
  function initRainbow(root = document) {
    if (!W.fx || !W.fx.rainbow) return;
    const els = [...root.querySelectorAll('[data-wf-rainbow]:not([data-wf-rainbow-bound])')];
    if (!els.length) return;
    els.forEach((el) => {
      el.dataset.wfRainbowBound = 'true';
      const c = el.dataset.wfRainbowBase || getComputedStyle(el).color;
      const stops = el.dataset.wfRainbowStops
        ? `${c} 36%, ${el.dataset.wfRainbowStops}, ${c} 62%`
        : `${c} 34%, #F7A29A 40%, #E6F59A 46%, #BFE2F5 51%, #8DB4F7 56%, #F9C6DB 61%, ${c} 67%`;
      el.style.backgroundImage = `linear-gradient(100deg, ${c} 0%, ${stops}, ${c} 100%)`;
      el.style.backgroundSize = '300% 100%';
      el.style.webkitBackgroundClip = 'text';
      el.style.backgroundClip = 'text';
      el.style.webkitTextFillColor = 'transparent';
      el.classList.add('is-rainbow');
    });
    W.scroll.add((state) => {
      els.forEach((el) => {
        const d = parseFloat(el.dataset.wfRainbowDistance) || 0.6;
        const t = Math.min(1, state.y / (state.vh * d));
        const q = Math.round(t * 400) / 400;
        el.style.backgroundPosition = `${100 - q * 100}% 0`;
      });
    });
  }

  W.rainbow = { init: initRainbow };

  /*
    Scroll reveal (Home DC componentDidMount): sections below the fold fade up once (opacity + 36px, 1s).
    Applies to .wf-section elements that start below the viewport, never to [data-wf-no-reveal] (hero).
  */
  function initReveal() {
    if (!W.fx || !W.fx.reveal || !('IntersectionObserver' in window)) return;
    const vh = window.innerHeight;
    const els = [...document.querySelectorAll('.wf-section:not([data-wf-no-reveal]):not([data-wf-revealed])')]
      .filter((el) => el.getBoundingClientRect().top > vh);
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => {
      el.dataset.wfRevealed = 'true';
      el.classList.add('wf-reveal');
      io.observe(el);
    });
  }

  // Paper grain from the design (wf-global "Paper grain"); static, not an effect.
  function initGrain() {
    if (!W.grain || document.querySelector('.wf-grain')) return;
    const grain = document.createElement('div');
    grain.className = 'wf-grain';
    grain.setAttribute('aria-hidden', 'true');
    document.body.prepend(grain);
  }

  // Season effects: load assets/wf-season-fx.js only when a page needs it.
  function initSeasonFx(scope = document) {
    if (!W.fx || !W.fx.season || !scope.querySelector('[data-wf-season-fx]')) return;
    if (W.seasonFx) return W.seasonFx.init(scope);
    if (W._seasonFxLoading || !W.assets || !W.assets.seasonFx) return;
    W._seasonFxLoading = true;
    const s = document.createElement('script');
    s.src = W.assets.seasonFx;
    s.defer = true;
    document.head.appendChild(s);
  }

  const ready = () => {
    initSeasonFx();
    initMoney();
    initGrain();
    initFlow();
    initRainbow();
    // Reveal after layout settled (DC: 400ms)
    setTimeout(initReveal, 400);
    initScrollRows();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
  document.addEventListener('shopify:section:load', (event) => {
    initMoney(event.target);
    initSeasonFx(event.target);
    initFlow();
    initRainbow(event.target);
    initScrollRows(event.target);
  });
})();
